import {
  CalendarDaysIcon,
  CheckCircle2Icon,
  DownloadIcon,
  RefreshCwIcon,
  XCircleIcon,
} from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadFileForm } from '@/components/forms/upload-file-form/upload-file-form';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { TONES } from '@/lib/tones';
import { SlotSheetSection } from '../../slot-sheet/slot-sheet-section';
import { ViewAdminAgendaSlotSheetViewModel } from '../view-admin-agenda-slot-sheet.viewmodel';

/**
 * Renders the "doctors working at the same time" figure, which the API reports
 * per half-day for all-day slots and as a single number otherwise.
 *
 * @param props.workAtTheSameTime - the figure carried by the sheet view model
 * @returns the value to display in the accounting list
 */
const WorkAtTheSameTimeValue = ({
  workAtTheSameTime,
}: {
  workAtTheSameTime: ViewAdminAgendaSlotSheetViewModel['information']['workAtTheSameTime'];
}): ReactNode => {
  const { t } = useTranslation();

  if (workAtTheSameTime === null) {
    return '-';
  }

  if (workAtTheSameTime.type === 'allday') {
    return (
      <span className="flex flex-col items-end">
        <span className="tabular-nums">
          {workAtTheSameTime.value.morning} {t('period-of-day.morning')}
        </span>
        <span className="tabular-nums">
          {workAtTheSameTime.value.afternoon} {t('period-of-day.afternoon')}
        </span>
      </span>
    );
  }

  return <span className="tabular-nums">{workAtTheSameTime.value}</span>;
};

/**
 * Yes / no / unknown marker for the "apply the assistant rate" accounting flag.
 *
 * @param props.isFeeApplicable - the flag, `null` while it has not been computed
 * @returns the marker element
 */
const IsFeeApplicableValue = ({
  isFeeApplicable,
}: {
  isFeeApplicable: boolean | null;
}) => {
  const { t } = useTranslation();

  if (isFeeApplicable === null) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (isFeeApplicable) {
    return (
      <span className={`inline-flex items-center gap-1 ${TONES.emerald.text}`}>
        <CheckCircle2Icon
          className="size-4"
          aria-hidden="true"
        />
        <span className="font-medium text-xs">{t('common.yes')}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <XCircleIcon
        className="size-4"
        aria-hidden="true"
      />
      <span className="font-medium text-xs">{t('common.no')}</span>
    </span>
  );
};

/**
 * "Comptabilité" section of the admin slot sheet: the partial Doctolib export
 * and the accounting figures computed for a confirmed slot.
 *
 * @param props.viewModel - the sheet view model
 * @returns the section element, or `null` when nothing is available yet
 */
export const ViewAdminAgendaSlotSectionAccounting = ({
  viewModel,
}: {
  viewModel: ViewAdminAgendaSlotSheetViewModel;
}) => {
  const { t } = useTranslation();

  const showImport = viewModel.doctolibImport.enabled;
  const showInformation = viewModel.setAccountingInformation.enabled;

  if (!showImport && !showInformation) {
    return null;
  }

  return (
    <SlotSheetSection
      icon={CalendarDaysIcon}
      title={t('view-admin-agenda-slot-sheet.accounting.title')}
    >
      <div className="space-y-4">
        {showImport && (
          <div className="space-y-2">
            <UploadFileForm
              label="view-admin-agenda-slot-sheet.accounting.doctolib-import.label"
              description="view-admin-agenda-slot-sheet.accounting.doctolib-import.description"
              btnLabel="view-admin-agenda-slot-sheet.accounting.doctolib-import.btn-import"
              accept=".csv"
              onUpload={({ files }) => {
                const [file] = files;
                viewModel.doctolibImport.api.importCsv({ file: file! });
                return true;
              }}
              loading={viewModel.doctolibImport.importing}
            />

            {viewModel.information.latestDoctolibImportAt && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-xs">
                  {t(
                    'view-admin-agenda-slot-sheet.accounting.doctolib-import.latest-import-on',
                    {
                      importedOn: viewModel.information.latestDoctolibImportAt,
                    },
                  )}
                </span>

                <Button
                  aria-label={t(
                    'view-admin-agenda-slot-sheet.accounting.export-csv-data.btn',
                  )}
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={viewModel.doctolibExport.api.exportCsv}
                  loading={viewModel.doctolibExport.exporting}
                  disabled={!viewModel.doctolibExport.enabled}
                >
                  <DownloadIcon
                    className="size-4"
                    aria-hidden="true"
                  />
                </Button>
              </div>
            )}
          </div>
        )}

        {showImport && showInformation && <Separator />}

        {showInformation && (
          <div className="space-y-3">
            <h4 className="font-medium text-sm">
              {t('view-admin-agenda-slot-sheet.accounting.information.label')}
            </h4>

            <div className="flex items-center justify-between gap-2 text-sm">
              <span>
                {t(
                  'view-admin-agenda-slot-sheet.accounting.information.is-fee-applicable',
                )}
              </span>
              <IsFeeApplicableValue
                isFeeApplicable={viewModel.information.isFeeApplicable}
              />
            </div>

            <div className="flex items-start justify-between gap-2 text-sm">
              <span>
                {t(
                  'view-admin-agenda-slot-sheet.accounting.information.doctors-working-at-the-same-time',
                )}
              </span>
              <span className="font-medium">
                <WorkAtTheSameTimeValue
                  workAtTheSameTime={viewModel.information.workAtTheSameTime}
                />
              </span>
            </div>

            <Button
              variant="secondary"
              className="w-full"
              onClick={viewModel.setAccountingInformation.api.submit}
              loading={viewModel.setAccountingInformation.loading}
            >
              <RefreshCwIcon
                className="size-4"
                aria-hidden="true"
              />
              {t(
                'view-admin-agenda-slot-sheet.accounting.information.btn-refresh',
              )}
            </Button>
          </div>
        )}
      </div>
    </SlotSheetSection>
  );
};
